import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * SEC-M-18: renders HTML that a tenant wrote in HRFlex (vacancy descriptions, the careers hero message).
 *
 * html-react-parser turns markup into React elements. React drops <script> and on* handlers, but it passes
 * `srcdoc` through as `srcDoc`, so `<iframe srcdoc="<script>...">` ran script on this origin - which every
 * tenant's careers pages share - for every anonymous candidate who opened the page, with no click needed.
 *
 * Sanitised here, at the one place tenant HTML is rendered, rather than trusted from the editor that wrote it:
 * DOMPurify keeps the formatting a job advert uses (headings, lists, links, tables, images) and removes
 * everything that can run, embed another document or change where the page submits.
 */
const PURIFY_CONFIG = {
  FORBID_TAGS: ["iframe", "frame", "object", "embed", "form", "input", "button", "style", "base", "meta", "link"],
  FORBID_ATTR: ["srcdoc", "formaction"],
};

/**
 * The inline-style properties the HRFlex editor (CKEditor) writes for a job advert's formatting: alignment, indent,
 * list numbering style, colour, emphasis and table/image sizing. HRFlex shows the advert with these
 * (`VacancyHtml` keeps safe CSS), so dropping them here made the careers page differ from what the recruiter
 * approved (work item 2544).
 *
 * Deliberately absent: position, inset/top/left, z-index, transform, display, opacity, content, and every
 * property that can take an image - so a description can neither cover the page nor fetch anything.
 * Longhand names, because the browser expands a shorthand (`text-decoration`) before this list is consulted.
 */
const ALLOWED_STYLE_PROPERTIES = new Set([
  "text-align",
  "text-indent",
  "margin-left",
  "margin-right",
  "padding-left",
  "padding-right",
  "list-style-type",
  "color",
  "background-color",
  "font-weight",
  "font-style",
  "font-size",
  "font-family",
  "text-decoration-line",
  "text-decoration-style",
  "text-decoration-color",
  "vertical-align",
  "width",
  "height",
  "border-collapse",
]);

// A value is plain words, numbers, units, hex colours, quoted font names and rgb()/hsl() colours - nothing that
// calls out (url(), image-set(), var(), expression()) or escapes its way past this test.
const SAFE_STYLE_VALUE = /^(?:[#\w\s.,%'"-]|(?:rgba?|hsla?)\([\d\s.,%/]*\))+$/i;

/**
 * Rebuilds each `style` attribute from the browser's own parse of it, keeping only allowed properties with safe
 * values. Reading the parsed declaration (not the raw text) means comments, escapes and odd spacing in the source
 * cannot smuggle a property past the allowlist.
 */
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (!(node instanceof HTMLElement) || !node.hasAttribute("style")) return;

  const kept: string[] = [];
  for (let i = 0; i < node.style.length; i++) {
    const property = node.style.item(i).toLowerCase();
    const value = node.style.getPropertyValue(property).trim();
    if (ALLOWED_STYLE_PROPERTIES.has(property) && value !== "" && SAFE_STYLE_VALUE.test(value)) {
      kept.push(`${property}: ${value}`);
    }
  }

  if (kept.length === 0) node.removeAttribute("style");
  else node.setAttribute("style", kept.join("; "));
});

export const sanitizeTenantHtml = (html: string | null | undefined): string =>
  html ? DOMPurify.sanitize(html, PURIFY_CONFIG) : "";

/**
 * The only author CSS inside an isolated document. Everything else is the browser's own default rendering - what
 * the recruiter saw in HRFlex - so there is no list of elements to keep restoring.
 */
const DOCUMENT_CSS = `
.tenant-document > :first-child { margin-top: 0; }
.tenant-document > :last-child { margin-bottom: 0; }
img { max-width: 100%; height: auto; }
`;

/**
 * Renders a tenant document inside a shadow root, which no page stylesheet can reach.
 *
 * Tailwind's base layer resets list markers, heading sizes, sub/sup, figure and dd indents, table attributes and
 * more on every element of the page. Overriding that element by element (the first fix for work item 2544) left
 * whatever the list missed still reset, and would need revisiting with every CSS change here. `all: revert` was
 * tried and refused: it also discards the HTML attributes CKEditor writes (`<table border="1">` lost its borders,
 * `<img width="120">` drew 0px wide). A shadow root keeps both; inherited properties - the page's font, colour and
 * line height - still flow in, so the advert reads as part of the page.
 *
 * Rendered through a portal, so React still owns the content. The shadow root is reused when it exists: StrictMode
 * runs effects twice in development, and a second attachShadow throws.
 */
const IsolatedHtml = ({ html }: { html: string | null | undefined }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [root, setRoot] = useState<ShadowRoot | null>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (host) setRoot(host.shadowRoot ?? host.attachShadow({ mode: "open" }));
  }, []);

  return (
    <div ref={hostRef} data-tenant-document="">
      {root &&
        createPortal(
          <>
            <style>{DOCUMENT_CSS}</style>
            <div className="tenant-document">{parse(sanitizeTenantHtml(html))}</div>
          </>,
          root,
        )}
    </div>
  );
};

/**
 * `isolate` renders the HTML as a document of its own (a job description), shielded from the page's CSS - see
 * IsolatedHtml. Without it the content renders inline, styled by the page, as the careers hero message is.
 */
export const SafeHtml = ({ html, isolate }: { html: string | null | undefined; isolate?: boolean }) =>
  isolate ? <IsolatedHtml html={html} /> : <>{parse(sanitizeTenantHtml(html))}</>;
